import { getIconPath } from '@/common'
import Data from '@/data'
import { InventorySlotType } from '@/data/enums'
import { itemGetBaseColor, itemGetName, itemIcon } from '@/logic/item'
import { WithItemTooltip } from '@/tooltip'
import { markupString } from '@/tooltip/common'
import { EquipmentVariant, Profile, StatPriority } from '@/types/profile'
import { CSSProperties, useMemo } from 'react'
import { getStatPriorityText, prioritySlots, useSlotStats } from '../planner/equipment/priority'
import './EquipmentStatPriority.scss'

function PrioritySectionView({
  slot,
  priority,
  variantIndex
}: {
  slot: InventorySlotType
  priority: {
    [key in InventorySlotType]?: StatPriority[]
  }
  variantIndex?: number
}) {
  const { item, stats } = useSlotStats(slot, variantIndex)

  const data = priority[slot]

  const base = item && Data.items[item.base]
  if (!base) return null
  const icon = itemIcon(item)
  const color = itemGetBaseColor(Data.items[item.base]) || item.rarity || 'normal'

  return data?.length ? (
    <div className="poe-priority-section">
      {item ? (
        <div className="poe-priority-item">
          <WithItemTooltip item={item}>
            <div>
              <div className={`poe-ItemDisplay poe-iw${base.inventory_width} poe-ih${base.inventory_height}`}>
                {!!icon && <div className="poe-img" style={{ backgroundImage: `url(${getIconPath(icon)})` }} />}
              </div>
            </div>
          </WithItemTooltip>
        </div>
      ) : undefined}

      <div className="poe-priority-main">
        <span className="poe-priority-section-title">
          {prioritySlots[slot]}:{' '}
          <span className={`poe-item-${color}`}>
            <WithItemTooltip item={item}>
              <span>{itemGetName(item)}</span>
            </WithItemTooltip>
          </span>
        </span>

        <ol className="poe-priority-section-list">
          {data
            ?.filter(({ id }) => getStatPriorityText(id, stats))
            .map(({ id }, index) => {
              const text = getStatPriorityText(id, stats)

              return (
                <li key={index} className="poe-planner-priority-list-item">
                  <span>{index + 1}. </span>
                  {text ? <span>{markupString(text)}</span> : null}
                </li>
              )
            })}
        </ol>
      </div>
    </div>
  ) : null
}

export function EquipmentStatPriority({
  planner,
  set,
  style
}: {
  planner: Profile
  set: string
  style: CSSProperties | undefined
}) {
  const allVariants = useMemo(() => planner.equipment.variants, [planner.equipment.variants])
  const { variant, index }: { variant: EquipmentVariant | undefined; index: number } = useMemo(() => {
    const index = allVariants.findIndex((variant, index) => variant.id === set || `${index}` === set)
    return { variant: allVariants[index], index }
  }, [allVariants, set])

  const priority = useMemo(() => variant?.priority ?? {}, [variant?.priority])

  return (
    <div className="poe-planner-stat-priority-embed" style={style}>
      {Object.keys(priority).map((slot) => (
        <PrioritySectionView key={slot} slot={slot as InventorySlotType} priority={priority} variantIndex={index} />
      ))}
    </div>
  )
}
